import React from 'react';
import { UI_THEME } from '../../config/constants';

interface ButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  variant?: 'primary' | 'secondary';
  disabled?: boolean;
  loading?: boolean;
  size?: 'sm' | 'md' | 'lg';
  style?: React.CSSProperties;
  className?: string;
}

export const Button: React.FC<ButtonProps> = ({
  children,
  onClick,
  type = 'button',
  variant = 'primary',
  disabled = false,
  loading = false,
  size = 'md',
  style = {},
  className = '',
}) => {
  const getButtonStyles = () => {
    const baseStyles = {
      border: 'none',
      borderRadius: UI_THEME.borderRadius.sm,
      fontSize: UI_THEME.fontSize.base,
      fontWeight: UI_THEME.fontWeight.medium,
      cursor: disabled || loading ? 'not-allowed' : 'pointer',
      opacity: disabled || loading ? 0.7 : 1,
      transition: 'all 0.2s ease',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: UI_THEME.spacing.sm,
    };

    const sizeStyles = {
      sm: {
        padding: `${UI_THEME.spacing.sm} ${UI_THEME.spacing.md}`,
        fontSize: UI_THEME.fontSize.sm,
      },
      md: {
        padding: `${UI_THEME.spacing.md} ${UI_THEME.spacing.xl}`,
        minWidth: '160px',
      },
      lg: {
        padding: `${UI_THEME.spacing.lg} ${UI_THEME.spacing.xxl}`,
        fontSize: UI_THEME.fontSize.lg,
      },
    };

    const variantStyles = {
      primary: {
        backgroundColor: disabled || loading ? UI_THEME.colors.gray[400] : UI_THEME.colors.primary,
        color: 'white',
      },
      secondary: {
        backgroundColor: UI_THEME.colors.background.primary,
        color: UI_THEME.colors.primary,
        border: `1px solid ${UI_THEME.colors.primary}`,
      },
    };

    return {
      ...baseStyles,
      ...sizeStyles[size],
      ...variantStyles[variant],
      ...style,
    };
  };

  const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!disabled && !loading) {
      if (style.backgroundColor === '#1d4ed8') {
        e.currentTarget.style.backgroundColor = '#1e40af';
      } else if (variant === 'primary') {
        e.currentTarget.style.backgroundColor = UI_THEME.colors.primaryHover;
      } else if (variant === 'secondary') {
        e.currentTarget.style.backgroundColor = '#f0f9ff';
      }
    }
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!disabled && !loading) {
      if (style.backgroundColor === '#1d4ed8') {
        e.currentTarget.style.backgroundColor = '#1d4ed8';
      } else if (variant === 'primary') {
        e.currentTarget.style.backgroundColor = UI_THEME.colors.primary;
      } else if (variant === 'secondary') {
        e.currentTarget.style.backgroundColor = UI_THEME.colors.background.primary;
      }
    }
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      style={getButtonStyles()}
      className={className}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {loading && (
        <div style={{
          width: '20px',
          height: '20px',
          border: '2px solid rgba(255, 255, 255, 0.3)',
          borderTop: '2px solid white',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
      )}
      {children}
    </button>
  );
};